/* Серверный режим «Свой аватар» (avatar) на движке шаблона (план вехи 3 §0 D1, план движений §2.2).
   createAvatar(createAvatarServer) (features/avatar/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SV спрашивает этот объект (SRV):
   пределы и цена, снимки и их проверки, голос, согласие, создание и хвост — только из сервиса заказа кадра (OrderService привязки EngineFrame).
   Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё S, свой мост загрузок, свой хвост BotRun (фасад TAIL пересоздаёт BotRun при смене кадра);
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис (run-driver.ts); при открытии
     ничего не отправляется, только показ;
   - S → входы: одна точка — зеркало (commit: syncFoot движка, смена стиля и голоса; inputs-mirror под замком не пишет, равные входы не пишет);
     входы → S — только когда входы сменились не зеркалом (повтор из «Истории», «Изменить» после перезагрузки, заявка родной формы):
     avatar-inputs.viewOf → applyView; id снимков пишет мост загрузок (лоток photo_01…photo_20: слоты подряд, убрать и заменить без новых
     загрузок соседей);
   - снимки идут серверу: лист прав (одна лишняя шторка, Q-M10), тип и вес по политике поля, одна очередь загрузок (media-bridge); проверка
     снимка — ответ сервера (GET /app/uploads/{id}: лицо, резкость, повтор, один человек; pending — опрос), на клиенте ничего не выдумывается;
     ответа нет — снимок считается, как у родной формы;
   - фото «Вы» показываются, как в шаблоне, а в заказ идут только с согласием (self_photos, Q-M8) и только если форма это поле публикует;
   - пределы (5…20, имя до 24) — constraints формы, затем документ экрана; цена — только цена формы (studioExpectedPrice), «Не хватает» — по
     остатку родной кнопки (GET /app/balance), без скидки (её у варианта нет); списание и чек — только из заказа (bot-run-server);
   - голос: свои клоны — GET /app/voices (неудачные не видны, обучающиеся не выбираются); «Голос из каталога» подтверждает сервер
     (voice-pick, только с переозвучкой); пример — preview_url сервера;
   - итог: приветствия варианта сервер не публикует (Q-M9) — кнопки проигрывания нет; «Сделать кружок», «Фото», «Видео» — переход в экран
     после сверки владельца (result-handoff); готовый вариант встаёт в «Вы» сам (сервер), страница перечитывает людей (syncPeople). */
import { endpoints } from '../../api';
import type { Voice } from '../../api/types';
import type { UserVoice } from '../../api/user-voices';
import { voiceOrb } from '../../bridge/voice-orb';
import { uiFormat, uiText } from '../../i18n';
import { syncPeople } from '../profile/people-server';
import { circleBalance, forgetCircleBalance, rememberCircleBalance } from '../studio/circle/tool-helpers';
import { cNum, tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import type { StudioApp, StudioForm } from '../studio/studio-api';
import type { StudioTarget } from '../studio/studio-navigation';
import { failText, receipt, serverFoot, esc, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaPolicy, type MediaBridge, type MediaItem, type MediaTray } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { watchPeople } from '../studio/engine-kit/people-bridge';
import { handoffTarget } from '../studio/engine-kit/result-handoff';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { screenDoc, type ScreenDoc } from '../studio/engine-kit/screen-doc';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { doneP, openSettings } from '../studio/engine-kit/tail-done';
import { uploadCheck, type UploadCheck } from '../studio/engine-kit/upload-checks';
import { createVoicePick, createVoiceSample, type VoicePick, type VoiceSample } from '../studio/engine-kit/voice-pick';
import { fieldOf } from '../shoot/kit/form-model';
import {
  applyView,
  avatarLimits,
  avatarMissing,
  avatarSlots,
  inputsOf,
  viewFromEngine,
  viewOf,
  type AvatarLimits,
  type AvatarViewHooks,
  type AvatarViewState,
} from './avatar-inputs';

/* ---------- контракт с движком (features/avatar/engine.ts, serve({...})) ---------- */

type Inputs = Record<string, string>;
/** Снимок мастера в S шаблона: {id, url, name, size, own, me, pos, z, st: chk | ok | bad, why, of} */
export interface EnginePhoto {
  id: string;
  st: 'chk' | 'ok' | 'bad';
  why: string | null;
  url?: string;
  name?: string;
  size?: number;
  own?: boolean;
  me?: boolean;
  of?: number;
  [key: string]: unknown;
}
/** Голос в виде шаблона (строка «Голос», лист «Голос варианта», шар на лице) */
export interface EngineVoice {
  id: number | string;
  name: string;
  orb: string;
  tags: string[];
  preview_url: string | null;
  st?: 'training' | 'ready';
  desc?: string;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  voices(): unknown[];
  myVoices(): unknown[];
  go?(page: string): void;
  [key: string]: unknown;
}
interface EngineFoot {
  poor(): boolean;
  sync(): void;
}
/** Крючки, которые движок отдаёт serve (engine.ts, блок SV после шагов): части экрана ниже по файлу — геттеры */
export interface AvatarEngineHooks extends AvatarViewHooks {
  S: Omit<AvatarViewState, 'photos'> & {
    step: number;
    max: number;
    photos: EnginePhoto[];
    fromMe: number;
    sh: { view: string; id: string | null } | null;
    playing: boolean;
    vgone: string;
    ret: boolean;
  };
  BASES: string[][];
  CHECKS: string[][];
  readonly FOOT: EngineFoot;
  readonly TAIL: BotRunApi;
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  mk(o: Record<string, unknown>): EnginePhoto;
  good(): number;
  nBad(): number;
  nChk(): number;
  face0(): EnginePhoto | null;
  byId(id: string): EnginePhoto | null;
  lack(n: number): string;
  cur(): number;
  vCur(): EngineVoice | null;
  setLimits(o: Partial<Omit<AvatarLimits, 'minutes'>>): void;
  limits(): Omit<AvatarLimits, 'minutes'>;
  importMe(): void;
  settled(p: EnginePhoto): void;
  paintPhotos(anim: boolean): void;
  paintVoice(): void;
  paintHead(): void;
  paintStage(): void;
  paintWho(): void;
  paintFace(): void;
  sheetPaint(): void;
  sheetClose(): void;
  go(n: number, dir?: number): void;
  liveStop(): void;
  vStop(quiet?: boolean): void;
  runExit(step: number): void;
  stepReady(): void;
  paintPage(): void;
  runSteps(): string[][];
}

export interface AvatarServer {
  ui<U extends object>(base: U): ServerUi<U>;
  verdict(p: EnginePhoto): string;
  remove(p: EnginePhoto): boolean;
  pick(files: ArrayLike<File> | null, anchor: Element | null, rep?: string): void;
  selfOk(): boolean;
  voice(id: unknown): EngineVoice | null;
  clones(): EngineVoice[];
  bases(L: string[][]): string[][];
  rc(): string;
  label(): string;
  busy(): boolean;
  missing(): string;
  missText(k: string): string;
  commit(): void;
  next(): void;
  pickVoice(id: unknown): void;
  sample(): void;
  sampleStop(): void;
  consent(on: boolean): void;
  born(r: BotRunRun): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null): boolean;
  open(o: unknown): void;
  close(): void;
  start(btn: HTMLElement | null): void;
  steps(): string[][];
  snap(): Record<string, unknown>;
  sentPrice(): number;
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
}

/* ---------- общие части ---------- */

interface Core {
  shake(el: Element | null, buzz?: () => void): void;
}
const core = () => window.BotCore as unknown as Core;
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
/** Подсказки шаблона (LACK) по кодам lack(); «few» — с пределом формы */
const LACK: Record<string, string> = {
  check: 'Дождитесь проверки фото',
  few: 'Нужно хотя бы {0} подходящих фото',
  name: 'Назовите вариант',
  ok: 'Отметьте согласие: на фото вы',
};
/** Правило листа прав перед первой загрузкой (Q-M10): права на снимки одним касанием */
const RULE = 'Только свои фото или с согласием человека на фото';
/** Ключи проверок сервера → ключи шаблона (CHECKS): лицо, резкость, повтор, один человек; другие провалы — без своей строки */
const CHECK_KEYS: Record<string, string> = {
  face: 'face',
  sharp: 'sharp',
  sharpness: 'sharp',
  resolution: 'sharp',
  min_side: 'sharp',
  duplicate: 'dup',
  single_person: 'one',
  one_person: 'one',
};
/** Опрос проверки снимка: шаг и предел (pending на сервере — около минуты) */
const POLL_MS = 1500;
const POLL_MAX = 40;
const num = (value: unknown): number | null => {
  const n = typeof value === 'string' && /^[1-9][0-9]*$/.test(value) ? Number(value) : value;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
};
function engineVoice(voice: Voice): EngineVoice {
  return {
    id: voice.id,
    name: voice.name,
    orb: voiceOrb(voice) ?? '',
    tags: Array.isArray(voice.tags) ? voice.tags : [],
    preview_url: voice.preview_url ?? null,
  };
}
/** Голос каталога оболочки (ui.voices / ui.myVoices) в виде шаблона */
function shellVoice(raw: unknown): EngineVoice | null {
  const v = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const id = num(v?.id);
  if (!v || id === null) return null;
  return {
    id,
    name: typeof v.name === 'string' ? v.name : '',
    orb: typeof v.orb === 'string' ? v.orb : '',
    tags: Array.isArray(v.tags) ? v.tags.filter((tag): tag is string => typeof tag === 'string') : [],
    preview_url: typeof v.preview_url === 'string' ? v.preview_url : null,
  };
}
/** Текст с разметкой: строка словаря экранируется, значения {n} — готовая разметка */
function htmlFormat(template: string, ...html: string[]): string {
  const text = uiFormat(template, ...html.map((_, i) => `${i}`));
  return esc(text).replace(/(\d+)/g, (_, i: string) => html[Number(i)] ?? '');
}
/** Итог проверки снимка сервера: '' — подходит, ключ шаблона — что не так ('x' — причина без своей строки), null — проверка ещё идёт */
export function verdictOf(check: UploadCheck | null | undefined): string | null {
  if (!check) return '';
  if (check.status === 'pending' || check.status === 'processing') return null;
  if (check.status !== 'ready') return 'x';
  const failed = check.checks.find(item => !item.ok);
  if (failed) return CHECK_KEYS[failed.key] ?? 'x';
  return check.reason ? 'x' : '';
}

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  driver: RunDriver;
  mirror: InputsMirror;
  /** Форма, для которой прошла первая сверка (пределы, входы → S) */
  form: StudioForm | null;
  /** Входы без id снимков, которые S уже знает (своя запись зеркала или перенос в S) */
  known: Inputs | null;
  intentSeen: string;
  formWait: boolean;
  formAt: StudioForm | null;
  prefill: Inputs | null;
  /** Снимок шаблона по записи моста и обратно */
  items: WeakMap<EnginePhoto, MediaItem>;
  pics: Map<string, EnginePhoto>;
  /** Опрос проверки по записи моста */
  polls: Map<string, number>;
  /** Превью снимков повтора читаются */
  restoring: boolean;
  /** Повтор принёс имя: когда шаг 1 пройдёт, мастер встанет на шаг 2 */
  wantStep2: boolean;
  /** Сервис вернул входы подстановки (перечитанная форма): лоток пишет свои слоты заново, а не снимается */
  keepTray: boolean;
  /** Место подсказок рамки загрузки (плитка, на которую выбрали файлы) */
  anchor: Element | null;
  paintQueued: { anim: boolean } | null;
  said: string;
  pick: VoicePick;
  sample: VoiceSample;
  voices: Map<number, EngineVoice>;
  clones: UserVoice[] | null;
  clonesAsk: boolean;
  peek: Intent | null;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  bornSeen: number | null;
  /** Готовый вариант показали и закрыли: следующий вход в кадр — мастер с начала */
  freshNext: boolean;
  /** Документ экрана (пределы, минуты) */
  doc: ScreenDoc<Record<string, unknown>>;
  /** Идёт переход в другой экран («Сделать кружок», «Фото», «Видео») */
  moving: AbortController | null;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createAvatarServer(h: AvatarEngineHooks): AvatarServer {
  const { S } = h;
  /** Начальное S шаблона: новый кадр начинает с него */
  const S0 = JSON.stringify(S);
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  let painted: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  let startSwap = false;
  const facade = {} as BotRunApi;

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.TAIL.run;
  const formOf = (f: Frame | null = cur() ?? frame) => f?.svc.get().form ?? null;
  const docOf = (f: Frame | null) => (f?.doc.data() ?? null) as Record<string, unknown> | null;
  /** Входы, которые определяют вид S: id снимков пишет мост — их смена S не трогает */
  const strip = (f: Frame, inputs: Inputs): Inputs => {
    const out = { ...inputs };
    for (const key of avatarSlots(f.svc.get().form)) delete out[key];
    return out;
  };
  /** Подсказка-стекло у элемента; элемента нет на месте — у кнопки подвала */
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('#avFoot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  /** Отказ у рамки загрузки: дрожь и подсказка (как addPhotos и readFiles шаблона) */
  const say = (text: string, at?: Element | null) => {
    const el = at?.isConnected ? at : (h.root()?.querySelector('#avUpW') ?? null);
    if (el) core().shake(el, () => h.ui().buzz());
    tip(el, text);
  };
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  const trayOf = (f: Frame): MediaTray | null => {
    const keys = avatarSlots(f.svc.get().form);
    return keys.length ? f.bridge.slots(keys) : null;
  };
  const stalled = (f: Frame) => {
    const { s } = live(f);
    return f.bridge.stalled() && !s.busy && s.error === TRANSPORT;
  };
  function recovery(f: Frame): ((btn: HTMLElement | null) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (stalled(f)) return btn => void busyButton(btn, () => f.bridge.resume());
    return null;
  }

  /* ---- пределы и цена ---- */
  /** Пределы формы и документа экрана → движок; сменились пределы снимков или имени — форма заново (цена — только кнопка) */
  function limits(f: Frame, form: StudioForm): boolean {
    const next = avatarLimits(form, docOf(f), f.svc.get().inputs);
    const was = h.limits();
    const changed = (['min', 'good', 'great', 'max', 'nameMax'] as const).some(key => was[key] !== next[key]);
    h.setLimits({ price: next.price, min: next.min, good: next.good, great: next.great, max: next.max, nameMax: next.nameMax });
    return changed;
  }
  const swapping = () => !!h.root()?.classList.contains('leave');

  /* ---- голоса ---- */
  function voice(id: unknown): EngineVoice | null {
    const f = cur() ?? frame;
    const want = num(id);
    if (want === null) return null;
    const picked = f?.voices.get(want);
    if (picked) return picked;
    const clone = clones().find(v => v.id === want);
    if (clone) return clone;
    const ui = h.ui();
    for (const list of [ui.voices?.() ?? [], ui.myVoices?.() ?? []])
      for (const raw of list) {
        const v = shellVoice(raw);
        if (v && v.id === want) return v;
      }
    return null;
  }
  /** Свои клоны (GET /app/voices): готовые с голосом каталога выбираются, обучающиеся видны, неудачные и удаляемые не показываются */
  function clones(): EngineVoice[] {
    const list = (cur() ?? frame)?.clones ?? [];
    const ui = h.ui();
    const orbOf = (id: number) => {
      for (const raw of ui.myVoices?.() ?? []) {
        const v = shellVoice(raw);
        if (v && v.id === id) return v.orb;
      }
      return '';
    };
    return list
      .filter(v => v.status !== 'failed' && v.status !== 'deleting')
      .map(v => {
        const ready = v.status === 'ready' && !!v.voice_id;
        /* шар клона — из самого клона (orb GET /app/voices): у обучающегося его нет в списке голосов каркаса, а шаблон рисует шар и ему (MYV.orb) */
        const own = v.orb ? (voiceOrb({ orb: v.orb, avatar_url: null }) ?? '') : '';
        return {
          id: ready ? v.voice_id! : `t${v.id}`,
          name: v.name,
          orb: (ready ? orbOf(v.voice_id!) : '') || own,
          tags: [],
          preview_url: null,
          st: ready ? 'ready' : 'training',
          desc: '',
        } satisfies EngineVoice;
      });
  }
  function loadClones(f: Frame) {
    if (f.clones || f.clonesAsk) return;
    f.clonesAsk = true;
    void endpoints.userVoices
      .list()
      .then(
        page => {
          f.clones = page.items;
        },
        () => {
          f.clones = [];
        },
      )
      .finally(() => {
        f.clonesAsk = false;
        if (cur() !== f || !onForm()) return;
        /* выбранный клон пропал (удалили) — выбор на «Позже», в листе голоса заметка (как событие bot:clones шаблона) */
        if (S.vm === 'clone' && S.voice != null && !clones().some(v => v.id === S.voice && v.st === 'ready')) {
          const gone = f.voices.get(Number(S.voice))?.name ?? '';
          S.vm = 'later';
          S.voice = null;
          S.vgone = gone;
        }
        h.paintVoice();
      });
  }
  /** Голос из входов, которого ещё нет в списках (повтор): сведения сервера для строки «Голос» */
  function loadVoice(f: Frame, id: number) {
    if (voice(id)) return;
    void endpoints
      .voiceDetails(id)
      .then(value => {
        if (value.id !== id || cur() !== f) return;
        f.voices.set(id, engineVoice(value));
        if (onForm()) h.paintVoice();
      })
      .catch(() => {
        /* голос недоступен: строка без имени, сервер решит при отправке */
      });
  }

  /* ---- снимки ---- */
  const isOwn = (f: Frame, p: EnginePhoto, list: MediaItem[]) => {
    const item = f.items.get(p);
    return !!item && list.includes(item);
  };
  /** Перерисовка снимков одним проходом после пачки событий моста */
  function paintSoon(f: Frame, anim: boolean) {
    if (f.paintQueued) {
      f.paintQueued.anim ||= anim;
      return;
    }
    f.paintQueued = { anim };
    queueMicrotask(() => {
      const q = f.paintQueued;
      f.paintQueued = null;
      if (cur() !== f || !onForm() || !q) return;
      h.paintPhotos(q.anim);
      h.sheetPaint();
      h.ui().buzz();
    });
  }
  /** Файл встал в лоток: новый снимок на проверке (или замена на месте снимка, чья запись ушла из лотка) */
  function put(f: Frame, item: MediaItem) {
    if (item.kind !== 'photo') return;
    const tray = trayOf(f);
    const list = tray?.list() ?? [];
    const stale = S.photos.find(p => !p.me && !isOwn(f, p, list));
    const p = h.mk({ url: item.url, name: item.name, size: item.size, own: false });
    f.items.set(p, item);
    f.pics.set(item.id, p);
    if (stale) {
      const gone = f.items.get(stale);
      if (gone) f.pics.delete(gone.id);
      p.id = stale.id;
      S.photos[S.photos.indexOf(stale)] = p;
      paintSoon(f, false);
    } else {
      S.photos.push(p);
      paintSoon(f, true);
    }
  }
  function drop(f: Frame, item: MediaItem | null) {
    const p = item ? f.pics.get(item.id) : null;
    if (!item || !p) return;
    f.pics.delete(item.id);
    stopPoll(f, item.id);
    S.photos = S.photos.filter(x => x !== p);
    if (S.sh?.view === 'photo' && S.sh.id === p.id) h.sheetClose();
    paintSoon(f, true);
    commit(f);
  }
  function stopPoll(f: Frame, id: string) {
    const timer = f.polls.get(id);
    if (timer !== undefined) window.clearTimeout(timer);
    f.polls.delete(id);
  }
  /** Проверка снимка ещё идёт (или её ответа нет): спросить сервер снова; ответа так и нет — снимок считается (как родная форма) */
  function poll(f: Frame, item: MediaItem, n = 0) {
    if (f.polls.has(item.id) || !item.uploadId) return;
    const timer = window.setTimeout(() => {
      f.polls.delete(item.id);
      if (frame !== f || !f.pics.has(item.id) || item.state !== 'ready') return;
      void endpoints
        .uploadStatus(item.uploadId!)
        .then(
          value => {
            if (frame !== f || (value.id && value.id !== item.uploadId)) return;
            item.check = uploadCheck(value);
          },
          () => {
            /* ответа нет — следующая попытка */
          },
        )
        .finally(() => {
          if (frame !== f || !f.pics.has(item.id)) return;
          const pending = !item.check || verdictOf(item.check) === null;
          if (pending && n + 1 < POLL_MAX) poll(f, item, n + 1);
          else settle(f, item, pending && item.check ? { ...item.check, status: 'rejected' } : item.check);
        });
    }, POLL_MS);
    f.polls.set(item.id, timer);
  }
  /** Итог проверки снимка на месте: плитка, циферблат, лицо, кнопка (settled шаблона) */
  function settle(f: Frame, item: MediaItem, check: UploadCheck | null) {
    const p = f.pics.get(item.id);
    if (!p) return;
    const w = verdictOf(check);
    if (w === null) {
      poll(f, item);
      return;
    }
    stopPoll(f, item.id);
    const was = `${p.st}|${p.why}`;
    p.st = w ? 'bad' : 'ok';
    p.why = w || null;
    if (was !== `${p.st}|${p.why}` && onForm()) h.settled(p);
    stepAfterRepeat(f);
  }
  function update(f: Frame, item: MediaItem) {
    if (item.state === 'ready' && f.pics.has(item.id)) {
      if (item.check) settle(f, item, item.check);
      /* ответа проверки нет: мост спросил один раз — спросим сами чуть позже */ else poll(f, item);
    }
    commit(f);
    if (onForm()) h.FOOT.sync();
  }
  /** Повтор с именем: шаг 1 пройден — мастер на шаге 2 (как «Изменить» шаблона) */
  function stepAfterRepeat(f: Frame) {
    if (!f.wantStep2 || !onForm() || S.step !== 1 || h.lack(1)) return;
    f.wantStep2 = false;
    h.go(2, 1);
  }
  /** Снимки в S — те, что у входов формы: id слотов сменились не мостом (повтор, заявка родной формы) — лоток заново по входам */
  function photos(f: Frame) {
    const tray = trayOf(f);
    if (!tray || f.restoring || f.bridge.pending() || f.svc.derive().locked) return;
    const s = f.svc.get();
    const want = avatarSlots(s.form)
      .map(key => s.inputs[key])
      .filter(Boolean);
    const have = tray.list().map(item => item.uploadId);
    if (want.length === have.length && want.every((id, i) => id === have[i])) {
      f.keepTray = false;
      return;
    }
    /* входы подстановки без снимков, а лоток их держит: снимки мастера остаются, слоты пишутся заново */
    if (f.keepTray && !want.length && tray.list().every(item => item.state === 'ready' && item.uploadId)) {
      f.keepTray = false;
      const keys = tray.keys();
      const list = tray.list();
      f.svc.setters.inputs(current => tray.inputsFor(current));
      f.svc.setters.uploadIds(current => {
        const next = { ...current };
        keys.forEach((key, i) => {
          if (list[i]?.uploadId) next[key] = list[i].uploadId!;
          else delete next[key];
        });
        return next;
      });
      return;
    }
    f.keepTray = false;
    for (const timer of f.polls.values()) window.clearTimeout(timer);
    f.polls.clear();
    f.pics.clear();
    S.photos = S.photos.filter(p => p.me);
    if (!f.svc.get().identity?.owner) return;
    f.restoring = true;
    void tray.restore().finally(() => {
      f.restoring = false;
      if (cur() !== f || !h.mounted()) return;
      paintSoon(f, false);
      commit(f);
      h.FOOT.sync();
    });
    if (onForm()) paintSoon(f, false);
  }
  /** Фото «Вы» в набор мастера (importMe шаблона: заменяет набор) — свои снимки лотка остаются за ними */
  function importSelf() {
    if (S.photos.some(p => p.me)) return;
    const own = S.photos.filter(p => !p.me);
    S.photos = [];
    h.importMe();
    S.photos.push(...own);
  }
  function dropSelf() {
    if (!S.photos.some(p => p.me)) return;
    S.photos = S.photos.filter(p => !p.me);
    S.fromMe = 0;
  }
  /** Мастер с начала (resetForm шаблона): S как при создании, снимки лотка сняты, фото «Вы» снова в наборе; входы — зеркалом */
  function resetWizard(f: Frame) {
    h.liveStop();
    for (const [key, value] of Object.entries(JSON.parse(S0) as Record<string, unknown>)) (S as unknown as Record<string, unknown>)[key] = value;
    const tray = trayOf(f);
    for (const item of tray?.list() ?? []) tray!.remove(item.id);
    for (const timer of f.polls.values()) window.clearTimeout(timer);
    f.polls.clear();
    f.pics.clear();
    f.wantStep2 = false;
    if (fieldOf(f.svc.get().form, 'self_photos')) importSelf();
    /* входы — сразу (страница заказа ещё может стоять, commit её ждёт): иначе прежние входы вернули бы прежний мастер */
    const s = f.svc.get();
    if (!s.form || f.svc.derive().locked) return;
    f.mirror.commit(prev => inputsOf(s.form, viewFromEngine(h, { rights: s.rights }), prev));
    f.known = strip(f, f.svc.get().inputs);
  }

  /* ---- входы ↔ S ---- */
  function commit(f: Frame | null = cur()) {
    if (!f || !f.form || !h.mounted() || h.TAIL.run || f.restoring) return;
    const s = f.svc.get();
    if (!s.form || f.svc.derive().locked) return;
    const v = viewFromEngine(h, { rights: s.rights });
    f.mirror.commit(prev => inputsOf(s.form, v, prev));
    limits(f, s.form);
    f.known = strip(f, f.svc.get().inputs);
  }
  /** Входы → S (повтор, «Изменить», заявка): имя, стиль, голос, согласие (только с правами), фото «Вы»; fresh — новый мастер без входов */
  function adopt(f: Frame, inputs: Inputs, fresh = false) {
    const form = f.svc.get().form;
    const v = viewOf(form, inputs);
    if (v.ok && !f.svc.get().rights) v.ok = false;
    applyView(h, v);
    if (v.voice && (v.vm === 'cat' || v.vm === 'clone')) loadVoice(f, v.voice);
    const published = !!fieldOf(form, 'self_photos');
    if (published && (v.self || (fresh && !avatarSlots(form).some(key => inputs[key])))) importSelf();
    else if (!v.self) dropSelf();
    f.wantStep2 = !!v.name && !fresh;
    f.known = strip(f, inputs);
    f.mirror.seen(inputs);
  }
  function formSync(f: Frame) {
    const { s, d } = live(f);
    const form = s.form;
    if (form && !f.form && f.formAt !== form) {
      /* первая форма: сервис ставит форму, затем её входы (подстановка повтора) отдельными шагами одного прохода — сверка после всех */
      if (!f.formWait) {
        f.formWait = true;
        queueMicrotask(() => {
          f.formWait = false;
          f.formAt = f.svc.get().form;
          changed(f);
        });
      }
      return;
    }
    if (form && f.form !== form) {
      const first = !f.form;
      f.form = form;
      const moved = limits(f, form);
      if (first) {
        const inputs = f.svc.get().inputs;
        adopt(f, inputs, !Object.keys(strip(f, inputs)).some(key => key !== 'consent_version' && key !== 'base'));
        f.prefill = f.known;
        if (onForm() && !swapping()) h.paintPage();
      } else if (moved && onForm() && !swapping()) h.paintPage();
    }
    if (!form) return;
    const it = intentOf(f);
    if (it && f.intentSeen !== it.key) {
      f.intentSeen = it.key;
      const inputs = (it.body.inputs ?? {}) as Inputs;
      if (!f.known || !sameInputs(strip(f, inputs), f.known)) adopt(f, inputs);
    }
    if (!d.locked && f.known && !sameInputs(strip(f, s.inputs), f.known)) {
      if (f.prefill && sameInputs(strip(f, s.inputs), f.prefill)) {
        /* сервис вернул входы подстановки (форма перечитана): мастер остаётся, зеркало и лоток пишут его заново */
        f.known = strip(f, s.inputs);
        f.mirror.seen(s.inputs);
        f.keepTray = true;
      } else {
        adopt(f, s.inputs);
        if (onForm() && !swapping()) h.paintPage();
      }
    }
    if (!onForm()) return;
    photos(f);
    /* права сняли (новая форма, «К настройкам», «Изменить»): отметка согласия шага 2 тоже снимается — её ставят заново */
    if (S.ok && !s.rights && !d.locked) {
      S.ok = null;
      const box = h.root()?.querySelector('[data-av-ok]');
      box?.classList.remove('on');
      box?.setAttribute('aria-checked', 'false');
    }
    const error = s.integrityError ? '' : s.error;
    if (error !== f.said) {
      f.said = error;
      if (FORM_SAY.includes(error)) say(uiText(error), f.anchor);
    }
    commit(f);
    h.FOOT.sync();
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    formSync(f);
    f.bridge.sync();
    f.driver.sync();
    balance(f);
  }
  /** Остаток для «Не хватает» — тот же, что у родной кнопки (GET /app/balance, память на минуту); пришёл — строка списания на месте */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() !== f || !onForm()) return;
        const rc = h.root()?.querySelector('#avRc');
        if (rc) rc.outerHTML = server.rc();
        h.FOOT.sync();
      });
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  /** Строки шаблона (заголовки страницы заказа, подписи кнопок подвала) — функциями словаря: BotRun берёт их через val при каждом показе */
  function texts(o: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(o)) {
      if (typeof value === 'string') out[key] = () => uiText(value);
      else if (Array.isArray(value) && value.length === 3 && typeof value[2] === 'string') out[key] = [value[0], value[1], () => uiText(value[2] as string)];
      else out[key] = value;
    }
    return out;
  }
  const baseName = (k: unknown) => h.BASES.find(b => b[0] === k)?.[1] ?? h.BASES[0][1];
  /** Карточка заказа (runWhat шаблона) словами профиля: стиль, снимки, голос */
  function cardText(sn: Record<string, unknown>): string {
    const n = Number(sn.n) || 0;
    return [baseName(sn.base), uiFormat('{0} фото', n), sn.vname ? uiFormat('голос {0}', String(sn.vname)) : uiText('без голоса')].join(' · ');
  }
  /** «Обычно около N минут» — только если документ экрана публикует минуты */
  function runP(): string {
    const minutes = num(docOf(cur() ?? frame)?.minutes);
    const tail = 'Можно закрыть: бот пришлёт вариант в чат, когда он будет готов.';
    return minutes ? uiFormat('Обычно около {0} минут. Можно закрыть: бот пришлёт вариант в чат, когда он будет готов.', minutes) : uiText(tail);
  }
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    const T = { ...((opts.T as Record<string, unknown>) ?? {}) };
    delete T.modWhy;
    delete T.runP;
    const foot = (opts.foot as Record<string, unknown>) ?? {};
    inst = create({
      ...opts,
      /* карточка заказа: стиль, снимки, голос — словами профиля */
      card: (r: BotRunRun) => (facade as unknown as { q(what: string): string }).q(cardText(r.snap)),
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      /* подвал отказов сервера; «Сделать кружок» и «Заменить фото» шаблона остаются */
      foot: serverFoot(texts(foot), 'av'),
      T: {
        ...texts(T),
        runP: () => runP(),
        doneP: (r: BotRunRun) => {
          const name = String(r.snap.name ?? '');
          const text = esc(uiFormat('«Вы · {0}» уже выбран за «Вы»: он в «Кто в кадре» на всех экранах, в каталоге персонажей и в «Профиле».', name));
          return doneP((cur() ?? frame)?.svc.get().order, text, 'av');
        },
      },
      /* «Что дальше»: «Фото» и «Видео» с вариантом за «Вы» — подписи словарём профиля */
      next: () => [
        ['data-av-photo', 'image', uiText('Фото')],
        ['data-av-video', 'video', uiText('Видео')],
      ],
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => server.sentPrice(),
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    if (!Object.hasOwn(facade, 'drop')) {
      facade.start = () => {
        inst?.start();
        if (!inst?.run) return;
        startSwap = true;
        window.setTimeout(() => {
          startSwap = false;
        }, 250);
      };
      facade.drop = () => {
        if (!startSwap) inst?.drop();
      };
      /* отрезок 3 степпера идёт за этапом через progress шаблона, а его зовёт sync BotRun только при ui.job — у сервера знак заказа
         публикует кадр (ui.job = null), поэтому шапку после хода этапа перерисовываем сами (paintHead шаблона, на месте) */
      facade.advance = (...args: Parameters<BotRunApi['advance']>) => {
        inst?.advance(...args);
        h.paintHead();
      };
    }
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) => (inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key](...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });
  const baseSnap = () => (tailOpts?.opts.snap as (() => Record<string, unknown>) | undefined)?.() ?? {};
  const baseSteps = () => (tailOpts?.opts.steps as (() => string[][]) | undefined)?.() ?? h.runSteps();

  /* ---- итог: переходы ---- */
  function exit(f: Frame, step: number) {
    void f.driver.leave(() => h.runExit(step), false, '');
  }
  async function handoff(f: Frame, el: HTMLElement, target: StudioTarget) {
    if (f.moving) return;
    const s = f.svc.get();
    const controller = new AbortController();
    f.moving = controller;
    const timer = window.setTimeout(() => controller.abort(), 30000);
    try {
      await busyButton(el, () => handoffTarget({ ownerId: s.intent?.owner ?? s.identity?.owner, target, signal: controller.signal }));
    } catch {
      if (f.moving === controller) tip(el, 'Владелец сессии изменился. Вернитесь в студию после обновления страницы.');
    } finally {
      window.clearTimeout(timer);
      if (f.moving === controller) f.moving = null;
    }
  }

  /* ---- привязка кадра ---- */
  let watching = false;
  function watch() {
    if (watching) return;
    watching = true;
    /* фото «Вы» пришли позже формы (вход в приложение): новый мастер без снимков берёт их, как шаблон при открытии */
    watchPeople({
      paint: () => {},
      mounted: () => !!cur() && onForm(),
      changed: () => {
        const f = cur();
        if (!f?.form || S.step !== 1 || S.photos.length || !fieldOf(f.form, 'self_photos')) return;
        h.importMe();
        if (!S.photos.length) return;
        h.paintPhotos(true);
        commit(f);
        h.FOOT.sync();
      },
    });
  }
  function bind(b: EngineBinding): EngineBound {
    watch();
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) dropFrame(frame);
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      /* «назад» внутри экрана: открытый лист мастера — закрыть; шаг 2 — на шаг 1 (родная форма: registerBack шага 2) */
      back() {
        if (cur() !== mine || !h.mounted() || h.TAIL.run) return false;
        if (S.sh) {
          h.sheetClose();
          return true;
        }
        if (S.step !== 2) return false;
        h.go(1, -1);
        return true;
      },
    };
  }
  function dropFrame(f: Frame) {
    f.bridge.cancel();
    f.pick.abort();
    f.sample.stop();
    f.doc.abort();
    f.moving?.abort();
    for (const timer of f.polls.values()) window.clearTimeout(timer);
    f.polls.clear();
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      mirror: createInputsMirror(b.svc),
      form: null,
      known: null,
      intentSeen: '',
      formWait: false,
      formAt: null,
      prefill: null,
      items: new WeakMap(),
      pics: new Map(),
      polls: new Map(),
      restoring: false,
      wantStep2: false,
      keepTray: false,
      anchor: null,
      paintQueued: null,
      said: '',
      pick: createVoicePick(),
      sample: createVoiceSample(),
      voices: new Map(),
      clones: null,
      clonesAsk: false,
      peek: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      bornSeen: null,
      freshNext: false,
      doc: screenDoc('avatar', {
        identity: 'avatar-screen',
        onChange: guard(() => {
          const form = f.svc.get().form;
          if (f.form && form && limits(f, form) && cur() === f && onForm() && !swapping()) h.paintPage();
        }),
      }),
      moving: null,
    });
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      policy: key => mediaPolicy(f.svc.get().form, key),
      say: guard((text: string) => say(text, f.anchor)),
      put: guard((item: MediaItem) => put(f, item)),
      drop: guard((item: MediaItem | null) => drop(f, item)),
      update: guard((item: MediaItem) => update(f, item)),
      consentWith: () => ({ texts: { title: 'Права и согласие', row: 'Права на материалы', sub: RULE } }),
    });
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.TAIL,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => {
        h.paintPage();
      },
      tip: (el, text) => tip(el, text),
    });
    return f;
  }

  const server: AvatarServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    verdict(p) {
      if (p.me) return '';
      const f = cur() ?? frame;
      return (f && verdictOf(f.items.get(p)?.check)) || '';
    },
    remove(p) {
      const f = cur();
      if (!f || h.TAIL.run) return false;
      const item = f.items.get(p);
      if (!item) {
        if (!f.svc.derive().locked) return true;
        say('Прежний заказ ещё не завершён. Сначала проверьте его результат.');
        return false;
      }
      const tray = trayOf(f);
      tray?.remove(item.id);
      if (tray?.list().includes(item)) return false;
      f.pics.delete(item.id);
      f.items.delete(p);
      stopPoll(f, item.id);
      return true;
    },
    pick(files, anchor, rep) {
      const f = cur();
      if (!f || !onForm()) return;
      const at = anchor ?? h.root()?.querySelector('#avUpW') ?? null;
      const L = Array.from(files ?? []).filter(file => /^image\//.test(file.type || 'image/'));
      if (!L.length) {
        say('Нужны фото: JPG или PNG', at);
        return;
      }
      const tray = trayOf(f);
      if (!tray) {
        say('Этот инструмент сейчас недоступен. Попробуйте обновить форму.', at);
        return;
      }
      f.anchor = at;
      if (rep) {
        const p = h.byId(rep);
        const item = p ? f.items.get(p) : undefined;
        const i = item ? tray.list().indexOf(item) : -1;
        if (i >= 0) void tray.replaceAt(i, L[0]);
        return;
      }
      const { max } = h.limits();
      const room = max - S.photos.length;
      if (room <= 0) {
        say(uiFormat('Уже {0} фото: больше не нужно', max), at);
        return;
      }
      void tray.pickSlots(L.slice(0, room));
      if (L.length > room) tip(at, uiFormat('Взяли {0} фото: всего нужно не больше {1}', room, max));
    },
    selfOk: () => !!fieldOf(formOf(), 'self_photos'),
    voice,
    clones,
    bases(L) {
      const field = fieldOf(formOf(), 'base');
      if (!field?.values.length) return L;
      const out = L.filter(b => field.values.includes(b[0]));
      return out.length ? out : L;
    },
    rc() {
      const form = formOf();
      const { price } = h.limits();
      if (!form || form.chargeable === false || price <= 0) return '';
      const sum = (n: number) => `<b class="mono">${cNum(n)} C</b>`;
      const bal = circleBalance();
      const have = bal !== null ? ` · ${htmlFormat('у вас {0}', sum(bal))}` : '';
      return `<p class="av-rc" id="avRc">${htmlFormat('Спишем {0} один раз', sum(price))}${have}</p>`;
    },
    label() {
      const f = cur();
      const next = S.step === 1;
      if (!f) return uiText(next ? 'Дальше' : 'Создать вариант');
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return uiText('Продолжить проверку');
      if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
      if (stalled(f)) return uiText('Продолжить загрузку');
      if (next) return uiText('Дальше');
      const { price } = h.limits();
      return price > 0 ? uiFormat('Создать вариант · {0} C', cNum(price)) : uiText('Создать вариант');
    },
    busy() {
      const f = cur();
      const s = f?.svc.get();
      return !!s && s.busy && !s.uploadingFile;
    },
    missing() {
      const f = cur();
      if (!f) return uiText('Проверяем доступность');
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return '';
      if (!s.identity && !s.integrityError) return uiText('Проверяем доступность');
      if (s.integrityError) return uiText(s.integrityError);
      if (!s.identity || s.identity.appId !== f.app.id) return uiText('Проверяем доступность');
      if (!s.form) return s.error ? '' : uiText('Проверяем доступность');
      if (s.versionStale) return '';
      if (!avatarSlots(s.form).length && !fieldOf(s.form, 'self_photos')) return uiText('Этот инструмент сейчас недоступен. Попробуйте обновить форму.');
      if (!s.form.available) return uiText('Создание сейчас недоступно');
      if (recovery(f)) return '';
      /* шаг 2 после перечитанной формы или неудачного превью: снимков может стать меньше — шаг 1 снова не пройден */
      const lack = h.lack(S.step) || (S.step === 2 ? h.lack(1) : '');
      if (lack) return lack;
      if (S.step === 1) return '';
      if (f.bridge.pending()) return uiText('Загружаем файл');
      const miss = avatarMissing(s.form, s.inputs);
      return miss ? uiText(miss) : '';
    },
    missText(k) {
      if (k === 'few') return uiFormat(LACK.few, h.limits().min);
      return LACK[k] ? uiText(LACK[k]) : k;
    },
    commit: () => commit(),
    next() {
      const f = cur();
      const btn = h.root()?.querySelector<HTMLElement>('#avFoot .btn') ?? null;
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.TAIL.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (S.step < 2) {
        if (!h.lack(1)) h.go(2, 1);
        return;
      }
      server.start(btn);
    },
    pickVoice(id) {
      const f = cur();
      const want = num(id);
      if (!f || !h.mounted() || h.TAIL.run || want === null || f.svc.derive().locked) return;
      const row = () => h.root()?.querySelector('#avVoiceRow') ?? null;
      f.pick
        .pick(want, { s2s: true })
        .then(picked => {
          if (!picked || cur() !== f || f.svc.derive().locked) return;
          if (!picked.ok) {
            tip(row(), 'Этот голос не умеет переозвучку, выберите другой');
            return;
          }
          f.voices.set(picked.voice.id, engineVoice(picked.voice));
          S.voice = picked.voice.id;
          S.vgone = '';
          S.vm = clones().some(v => v.id === picked.voice.id && v.st === 'ready') ? 'clone' : 'cat';
          h.vStop(true);
          h.paintVoice();
          h.ui().buzz();
        })
        .catch(() => tip(row(), 'Не удалось выбрать голос. Попробуйте ещё раз.'));
    },
    sample() {
      const f = cur();
      const v = h.vCur();
      if (!f || !v) return;
      if (S.playing) {
        f.sample.stop();
        S.playing = false;
        h.paintFace();
        h.ui().buzz();
        return;
      }
      const ok = f.sample.play(v.preview_url, () => {
        S.playing = false;
        if (cur() === f && onForm()) h.paintFace();
      });
      if (!ok) {
        tip(h.root()?.querySelector('.av-vb') ?? null, 'Пример голоса пока недоступен');
        return;
      }
      S.playing = true;
      h.paintFace();
      h.ui().buzz();
    },
    sampleStop() {
      (cur() ?? frame)?.sample.stop();
    },
    consent(on) {
      const f = cur();
      if (!f || f.svc.derive().locked) return;
      f.svc.setters.rights(on);
    },
    born(r) {
      const f = cur() ?? frame;
      const id = f?.svc.derive().orderId;
      if (!f || !id || r.st !== 'done' || f.bornSeen === id) return;
      f.bornSeen = id;
      /* готовый вариант встаёт в «Вы» на сервере: ряд лиц, каталог и «Профиль» перечитывают людей */
      void syncPeople().catch(() => {});
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    click(d, el, r) {
      const f = cur();
      const keys = ['avRetry', 'avRchk', 'avFix', 'avSettings', 'avHplay', 'avCirc', 'avPhoto', 'avVideo', 'avRepl', 'avEdit'];
      if (!keys.some(key => d[key] !== undefined)) return false;
      if (!f) return true;
      if (d.avSettings !== undefined) {
        openSettings();
        return true;
      }
      /* приветствия варианта сервер не публикует (Q-M9) */
      if (d.avHplay !== undefined) return true;
      const target: StudioTarget | null =
        d.avCirc !== undefined
          ? { screen: 'circle' }
          : d.avPhoto !== undefined
            ? { screen: 'photo', options: { mode: 'text' } }
            : d.avVideo !== undefined
              ? { screen: 'video', options: { mode: 'text' } }
              : null;
      if (target) {
        if (r?.st === 'done') void handoff(f, el, target);
        return true;
      }
      if (f.driver.busy()) return true;
      if (d.avRetry !== undefined) h.TAIL.retry();
      else if (d.avRchk !== undefined) void f.driver.check(el);
      /* «Заменить фото» — к шагу 1, «К настройкам» и «Изменить» — к шагу 2: те же входы, без нового заказа (D10) */ else exit(f, d.avRepl !== undefined ? 1 : 2);
      return true;
    },
    open(o) {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.paintPage();
        return;
      }
      const fresh = painted !== f;
      if (fresh) {
        /* другой кадр или новый сервис под прежним ключом: S, снимки, голос и хвост прежнего сюда не переходят */
        h.liveStop();
        for (const [key, value] of Object.entries(JSON.parse(S0) as Record<string, unknown>)) (S as unknown as Record<string, unknown>)[key] = value;
        rebuild();
        painted = f;
        f.form = null;
        f.known = null;
        f.intentSeen = '';
      }
      f.formShown = false;
      /* готовый вариант уже закрыт (сверка прошла, пока экран был закрыт): мастер с начала */
      if (f.freshNext && !f.driver.busy() && !f.svc.get().intent) {
        f.freshNext = false;
        resetWizard(f);
      }
      const root = h.root();
      if (h.TAIL.run && root) {
        h.TAIL.show(root);
        changed(f);
        return;
      }
      /* вход экрана (setup шаблона): откуда пришли; лист закрыт */
      S.ret = !!(o && typeof o === 'object' && (o as Record<string, unknown>).ret);
      S.sh = null;
      void f.doc.load();
      loadClones(f);
      f.peek = peekIntent(f.app.id);
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      f.pick.abort();
      f.sample.stop();
      /* показанный готовый вариант закрывает заказ (шаблон: следующий вход начинает мастер заново): свежая сверка заказа (returnToForm),
         просмотрено; вход в тот же кадр — мастер с начала (снова с фото «Вы»). Не подтвердился — следующий вход снова на хвосте */
      if (h.TAIL.run?.st === 'done' && f.svc.derive().orderId) {
        f.freshNext = true;
        void f.driver.leave(
          () => {
            f.freshNext = false;
            resetWizard(f);
            h.runExit(1);
          },
          false,
          '',
        );
      }
    },
    start(btn) {
      const f = cur();
      if (!f) return;
      commit(f);
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.TAIL.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (h.FOOT.poor()) {
        /* «Не хватает N C · Пополнить»: раздел «Баланс» (скидки у варианта нет — листа «Не хватает Circle» тоже) */
        const u = h.ui();
        if (typeof u.go === 'function') u.go('balance');
        else window.location.hash = '#balance';
        return;
      }
      /* отметка согласия без прав (права сняли): отметка заново */
      if (!s.rights && S.ok) {
        S.ok = null;
        h.paintPage();
        tip(h.root()?.querySelector('[data-av-ok]') ?? null, LACK.ok);
        return;
      }
      h.liveStop();
      h.TAIL.start();
    },
    steps: () => baseSteps(),
    snap() {
      const f = cur();
      const sn = baseSnap();
      const it = intentOf(f);
      /* заявка после перезагрузки: снимков в S ещё нет — имя, стиль и число снимков из входов заявки */
      if (it && f && !S.photos.length) {
        const inputs = (it.body.inputs ?? {}) as Inputs;
        const v = viewOf(f.svc.get().form, inputs);
        if (v.name) sn.name = v.name;
        if (v.base) sn.base = v.base;
        sn.n = avatarSlots(f.svc.get().form).filter(key => inputs[key]).length;
      }
      return { ...sn, cheap: false };
    },
    sentPrice: () => cur()?.svc.derive().price ?? 0,
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const steps = tailSteps(order);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: {
          modH: 'Вариант не создан',
          techH: 'Не получилось создать вариант',
          notMade: uiText('вариант не создан'),
          modWhy: uiText('На снимках публичный человек или человек без согласия: такие варианты не делаем.'),
        },
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      return receipt(r, f?.svc.get().order ?? null, { cheap: false, where: '' });
    },
  };

  /* ---- смена языка профиля ---- */
  function relabel(r: BotRunRun) {
    const fresh = server.steps();
    for (const step of r.steps) {
      const same = fresh.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  function relocale() {
    const r = h.TAIL.run;
    if (r) relabel(r);
    const f = cur();
    const root = h.root();
    if (!f || !root || !h.mounted()) return;
    if (h.TAIL.run) h.TAIL.show(root);
    else h.paintPage();
    changed(f);
  }
  document.addEventListener('steos:locale', relocale);
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.FOOT.sync();
  });
  instance = { bind };
  return server;
}

/** ENGINES.avatar.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindAvatarEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «avatar» реестра движков студии: движок шаблона window.BotAvatar, атрибуты data-av-*, с правой панелью (голос варианта из каталога);
    знак заказа — avatar, как у родного хвоста */
export const AVATAR_ENGINE: EngineScreen = { global: 'BotAvatar', prefix: 'av', side: true, kind: 'avatar', bind: bindAvatarEngine };

export function registerAvatarEngine() {
  ENGINES.avatar = AVATAR_ENGINE;
}
